import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { serviceCharge, DELIVERY_ZONES } from "../lib/constants";

const CartContext = createContext(null);
const CART_KEY = "ayome_cart";
const ZONE_KEY = "ayome_zone";

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [zone, setZone] = useState("Dorchester");

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(CART_KEY) || "[]");
      setItems(saved);
    } catch (e) {}
    const z = localStorage.getItem(ZONE_KEY);
    if (z) setZone(z);
  }, []);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  }, [items]);

  const setDeliveryZone = (z) => {
    setZone(z);
    localStorage.setItem(ZONE_KEY, z);
  };

  const addItem = (product) => {
    setItems((prev) => {
      const found = prev.find((i) => i.product_id === product.id);
      if (found) {
        return prev.map((i) =>
          i.product_id === product.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          product_name: product.name,
          price: product.price,
          unit: product.unit,
          quantity: 1,
        },
      ];
    });
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) return removeItem(productId);
    setItems((prev) =>
      prev.map((i) => (i.product_id === productId ? { ...i, quantity } : i))
    );
  };

  const removeItem = (productId) =>
    setItems((prev) => prev.filter((i) => i.product_id !== productId));

  const addItems = (orderItems) => {
    setItems((prev) => {
      const next = [...prev];
      orderItems.forEach((oi) => {
        const found = next.find((i) => i.product_id === oi.product_id);
        if (found) found.quantity += oi.quantity;
        else
          next.push({
            product_id: oi.product_id,
            product_name: oi.product_name,
            price: oi.price,
            unit: oi.unit || "",
            quantity: oi.quantity,
          });
      });
      return next;
    });
  };

  const clearCart = () => setItems([]);

  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.price * i.quantity, 0),
    [items]
  );
  const deliveryCharge = useMemo(() => {
    const z = DELIVERY_ZONES.find((d) => d.name === zone);
    return z && items.length ? z.charge : 0;
  }, [zone, items.length]);
  const itemCount = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);
  const fixedCharge = useMemo(() => (subtotal > 0 ? serviceCharge(subtotal) : 0), [subtotal]);
  const total = subtotal + fixedCharge + deliveryCharge;

  return (
    <CartContext.Provider
      value={{
        items,
        zone,
        setDeliveryZone,
        addItem,
        addItems,
        updateQuantity,
        removeItem,
        clearCart,
        subtotal,
        fixedCharge,
        deliveryCharge,
        total,
        itemCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
